<template>
  <div class="checks-page">
    <Tabs
      class="checks-tabs"
      :tabs="tabs"
      :tabSelected="tabSelected"
      :actionToAlterTab="actionToAlterTab"
      :noPadding="true"
      gapTabItem="0"
      @activeIdxTab="tabSelected = $event"
    >
      <template v-slot:register>
        <h5 class="page-section-title">Cadastro de Cheques</h5>
        <div class="form-card">
          <div
            v-if="check.id"
            class="editing-banner"
            :class="`editing-${statusClass(check.situacao)}`"
          >
            <div class="editing-information">
              <b-icon-pencil-square />
              <div>
                <small>Você está editando</small>
                <strong>Cheque Nº {{ check.numero || "--" }}</strong>
              </div>
            </div>
            <div class="editing-status">
              <span>Situação atual</span>
              <strong class="status" :class="statusClass(check.situacao)">
                {{ check.situacao }}
              </strong>
            </div>
          </div>

          <div class="required-fields">
            <small
              >Os campos marcados com <strong>*</strong> são obrigatórios</small
            >
          </div>

          <section class="form-section check-information">
            <div class="section-heading">
              <span class="section-icon"><b-icon-credit-card2-front /></span>
              <div>
                <h6>Informações do cheque</h6>
                <small>Identificação, valores e controle financeiro</small>
              </div>
            </div>

            <div class="form-grid general-fields">
              <div class="field">
                <Input
                  label="Nº Cheque"
                  :labelFixed="true"
                  type="number"
                  :dataInput.sync="check.numero"
                  ref="numero"
                  required
                />
              </div>

              <div class="field">
                <Input
                  label="Agência *"
                  :labelFixed="true"
                  type="number"
                  :dataInput.sync="check.agencia"
                  ref="agencia"
                  required
                />
              </div>

              <div class="field">
                <Input
                  label="Conta"
                  :labelFixed="true"
                  type="number"
                  :dataInput.sync="check.conta"
                  ref="conta"
                  required
                />
              </div>

              <div class="field">
                <Input
                  label="Díg."
                  :labelFixed="true"
                  type="number"
                  :dataInput.sync="check.digito"
                  ref="digito"
                  required
                />
              </div>

              <div class="field">
                <InputMoney
                  label="Valor Cheque"
                  labelFixed
                  ref="valor"
                  type="text"
                  :dataInput.sync="check.valor"
                />
              </div>

              <div class="field">
                <Input
                  ref="dataEntrada"
                  label="Entrada *"
                  required
                  type="date"
                  :dataInput.sync="check.dataEntrada"
                  :labelFixed="true"
                />
              </div>

              <div class="field">
                <Input
                  ref="dataBomPara"
                  label="Bom Para *"
                  required
                  type="date"
                  :dataInput.sync="check.dataBomPara"
                  :labelFixed="true"
                />
              </div>

              <div class="field">
                <InputSelect
                  label="Centro de Custos"
                  :items="costCenters"
                  valueField="id"
                  textField="nome"
                  :dataSelect.sync="check.idCentroCusto"
                  :openCreateModal="() => $bvModal.show('modalCheckCostCenter')"
                  ref="idCentroCusto"
                />
              </div>

              <div class="field">
                <InputSelect
                  label="Banco"
                  :items="banks"
                  valueField="id"
                  textField="nome"
                  :dataSelect.sync="check.idBanco"
                  :openCreateModal="() => $bvModal.show('modalCheckBank')"
                  ref="idBanco"
                />
              </div>

              <div class="field">
                <InputSelect
                  label="Situação"
                  :items="situations"
                  valueField="id"
                  textField="nome"
                  :dataSelect.sync="check.situacao"
                  :openCreateModal="() => $bvModal.show('modalCheckSituation')"
                  ref="situacao"
                />
              </div>

              <div class="field">
                <InputSelect
                  label="Funcionário"
                  :items="employees"
                  valueField="id"
                  textField="nomeFuncionario"
                  :dataSelect.sync="check.idFuncionario"
                  :openCreateModal="() => $bvModal.show('modalCheckEmployee')"
                  ref="idFuncionario"
                />
              </div>
            </div>
          </section>

          <div class="parties-grid">
            <section class="form-section party-card receipt-card">
              <div class="section-heading compact">
                <span class="section-icon"><b-icon-box-arrow-in-down /></span>
                <div>
                  <h6>Recebimento</h6>
                  <small>Origem e titular do cheque</small>
                </div>
              </div>

              <PartyFields
                title="Tipo de Recebimento"
                :partyType.sync="check.tipoRecebimento"
                :selectedName="receiptName"
                :otherName.sync="check.outrosRecebimento"
                @choose="choosePartyType('receipt', $event)"
              />
              <div class="field full-field observation-field">
                <label>Observação do recebimento</label>
                <textarea v-model="check.observacaoRecebimento" rows="5" />
              </div>
            </section>

            <section class="form-section party-card destination-card">
              <div class="section-heading compact">
                <span class="section-icon"><b-icon-box-arrow-up-right /></span>
                <div>
                  <h6>Destino</h6>
                  <small>Destinação final do cheque</small>
                </div>
              </div>

              <PartyFields
                title="Tipo de Destino"
                :partyType.sync="check.tipoDestino"
                :selectedName="destinationName"
                :otherName.sync="check.outrosDestino"
                @choose="choosePartyType('destination', $event)"
              />
              <div class="field full-field observation-field">
                <label>Observação do destino</label>
                <textarea v-model="check.observacaoDestino" rows="5" />
              </div>
            </section>
          </div>

          <div class="floating-actions">
            <button class="action-button secondary" @click="newCheck">
              Novo
              <b-icon-arrow-clockwise />
            </button>
            <div class="action-buttons">
              <button
                class="action-button secondary"
                disabled
                title="Impressão disponível em uma próxima versão"
              >
                <b-icon-printer />
                Imprimir
              </button>
              <button
                class="action-button primary"
                :disabled="saving"
                @click="save"
              >
                <b-spinner v-if="saving" small />
                <b-icon-save v-else />
                {{ check.id ? "Atualizar" : "Salvar" }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:search>
        <h5 class="page-section-title">Pesquisa de Cheques</h5>
        <div class="search-card">
          <div class="search-toolbar">
            <div class="search-filters">
              <div class="field">
                <label>Pesquisar por</label>
                <select v-model="filters.type">
                  <option value="">Todos</option>
                  <option value="numero">Nº Cheque</option>
                  <option value="participante">Cliente/Fornecedor</option>
                  <option value="banco">Banco</option>
                  <option value="situacao">Situação</option>
                </select>
              </div>
              <div class="field search-text">
                <label>Termo</label>
                <input
                  v-model="filters.q"
                  type="text"
                  placeholder="Pesquisar"
                  @keyup.enter="searchChecks(1)"
                />
              </div>
              <div class="field">
                <label>Início</label>
                <input v-model="filters.startDate" type="date" />
              </div>
              <div class="field">
                <label>Final</label>
                <input v-model="filters.endDate" type="date" />
              </div>
              <button class="filter-button" @click="searchChecks(1)">
                <b-icon-search />
              </button>
            </div>
            <div class="records-counter">
              <b-icon-credit-card2-front />
              Cheques Registrados - {{ total }}
            </div>
          </div>

          <div class="table-responsive check-table-wrapper">
            <table class="check-table">
              <thead>
                <tr>
                  <th>Nº Cheque</th>
                  <th>Entrada</th>
                  <th>Bom Para</th>
                  <th>Recebido de</th>
                  <th>Banco</th>
                  <th>Valor</th>
                  <th>Situação</th>
                  <th class="actions-heading">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in checks"
                  :key="item.id"
                  class="check-row"
                  tabindex="0"
                  @click="edit(item.id)"
                  @keyup.enter="edit(item.id)"
                >
                  <td class="check-number">{{ item.numero }}</td>
                  <td>{{ formatDate(item.dataEntrada) }}</td>
                  <td>{{ formatDate(item.dataBomPara) }}</td>
                  <td>{{ item.nomeRecebimento || "--" }}</td>
                  <td>{{ item.banco || "--" }}</td>
                  <td>{{ formatCurrency(item.valor) }}</td>
                  <td>
                    <span class="status" :class="statusClass(item.situacao)">{{
                      item.situacao
                    }}</span>
                  </td>
                  <td class="actions-cell">
                    <div class="row-actions">
                      <button
                        title="Excluir"
                        class="delete"
                        @click.stop="openCheckDeleteConfirmation(item.id)"
                        @keyup.enter="$event.stopPropagation()"
                      >
                        <b-icon-trash-fill />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="loadingSearch">
                  <td colspan="8" class="empty">
                    <b-spinner small /> Carregando...
                  </td>
                </tr>
                <tr v-else-if="!checks.length">
                  <td colspan="8" class="empty">Nenhum cheque encontrado.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="table-footer">
            <div class="pagination">
              <button
                :disabled="filters.page === 1"
                @click="searchChecks(filters.page - 1)"
              >
                <b-icon-chevron-left />
              </button>
              <span>{{ filters.page }}</span>
              <button
                :disabled="filters.page >= totalPages"
                @click="searchChecks(filters.page + 1)"
              >
                <b-icon-chevron-right />
              </button>
            </div>
          </div>
        </div>
      </template>
    </Tabs>

    <PartySelectionModal
      modalId="modalCheckParty"
      :partyType="partyModalType"
      @selected="selectParty"
    />
    <ModalConfirm
      modalName="modal-confirm-check-delete"
      title="Deseja realmente excluir ?"
      subtitle="Atenção! Esta ação não poderá ser desfeita. Ao confirmar, o cheque será excluído permanentemente."
      leftButtonText="Não excluir"
      rightButtonText="Sim"
      headerIcon="b-icon-exclamation-triangle-fill"
      :rightButtonClick="removeCheck"
      @modalClosed="selectedCheckId = ''"
    />
    <CheckCatalogModal
      modalId="modalCheckBank"
      catalogType="banks"
      title="Banco"
      @created="catalogCreated('banks', $event)"
      @updated="catalogUpdated('banks', $event)"
      @deleted="catalogDeleted('banks', $event)"
    />
    <CheckCatalogModal
      modalId="modalCheckCostCenter"
      catalogType="cost-centers"
      title="Centro de Custos"
      @created="catalogCreated('cost-centers', $event)"
      @updated="catalogUpdated('cost-centers', $event)"
      @deleted="catalogDeleted('cost-centers', $event)"
    />
  </div>
</template>

<script>
import moment from "moment";
import api from "../../services/axios";
import ServiceCheck from "../../services/serviceCheck";
import ServiceCheckBank from "../../services/serviceCheckBank";
import ServiceCheckCostCenter from "../../services/serviceCheckCostCenter";
import Tabs from "../UI/Tabs/Tab.vue";
import PartySelectionModal from "./PartySelectionModal.vue";
import CheckCatalogModal from "./CheckCatalogModal.vue";
import ModalConfirm from "../Modals/ModalConfirm.vue";
import Input from "../UI/CustomInputs/Input.vue";
import InputMoney from "../UI/CustomInputs/InputMoney.vue";
import InputSelect from "../UI/CustomInputs/InputSelect.vue";
import PartyFields from "./PartyFields.vue";

const emptyCheck = () => ({
  id: "",
  numero: "",
  agencia: "",
  conta: "",
  digito: "",
  valor: null,
  dataEntrada: moment().format("YYYY-MM-DD"),
  dataBomPara: moment().format("YYYY-MM-DD"),
  idBanco: "",
  idCentroCusto: "",
  idFuncionario: "",
  situacao: "Em aberto",
  tipoRecebimento: "Outros",
  idClienteRecebimento: "",
  idFornecedorRecebimento: "",
  outrosRecebimento: "",
  observacaoRecebimento: "",
  tipoDestino: "Outros",
  idClienteDestino: "",
  idFornecedorDestino: "",
  outrosDestino: "",
  observacaoDestino: "",
});

export default {
  components: {
    Tabs,
    PartySelectionModal,
    CheckCatalogModal,
    PartyFields,
    ModalConfirm,
    Input,
    InputMoney,
    InputSelect,
  },
  data() {
    return {
      check: emptyCheck(),
      receiptName: "",
      destinationName: "",
      employees: [],
      banks: [],
      costCenters: [],
      checks: [],
      total: 0,
      tabs: [
        { name: "register", text: "Cadastro" },
        { name: "search", text: "Pesquisar" },
      ],
      situations: ["Em aberto", "Pago", "Compensado", "Devolvido", "Cancelado"],
      filters: { q: "", type: "", startDate: "", endDate: "", page: 1 },
      tabSelected: 1,
      actionToAlterTab: false,
      selectedCheckId: "",
      partyContext: "receipt",
      partyModalType: "Cliente",
      saving: false,
      loadingSearch: false,
    };
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil(this.total / 9), 1);
    },
  },
  methods: {
    selectTab(index) {
      this.tabSelected = index;
      this.actionToAlterTab = !this.actionToAlterTab;
    },
    choosePartyType(context, type) {
      this.partyContext = context;
      const suffix = context === "receipt" ? "Recebimento" : "Destino";
      this.check[`tipo${suffix}`] = type;
      this.check[`idCliente${suffix}`] = "";
      this.check[`idFornecedor${suffix}`] = "";
      if (context === "receipt") this.receiptName = "";
      else this.destinationName = "";

      if (type !== "Outros") {
        this.partyModalType = type;
        this.$nextTick(() => this.$bvModal.show("modalCheckParty"));
      }
    },
    selectParty(party) {
      const suffix =
        this.partyContext === "receipt" ? "Recebimento" : "Destino";
      const type = this.check[`tipo${suffix}`];
      this.check[`idCliente${suffix}`] = type === "Cliente" ? party.id : "";
      this.check[`idFornecedor${suffix}`] =
        type === "Fornecedor" ? party.id : "";
      if (this.partyContext === "receipt") this.receiptName = party.name;
      else this.destinationName = party.name;
    },
    validate() {
      const required = [
        [this.check.numero, "Nº do cheque"],
        [this.check.agencia, "Agência"],
        [this.check.dataEntrada, "Entrada"],
        [this.check.dataBomPara, "Bom Para"],
      ];
      const missing = required.find(([value]) => !value);
      if (missing) return `O campo ${missing[1]} é obrigatório`;

      for (const context of ["Recebimento", "Destino"]) {
        const type = this.check[`tipo${context}`];
        if (type === "Cliente" && !this.check[`idCliente${context}`])
          return `Selecione o cliente do ${context.toLowerCase()}`;
        if (type === "Fornecedor" && !this.check[`idFornecedor${context}`])
          return `Selecione o fornecedor do ${context.toLowerCase()}`;
      }
      return "";
    },
    async save() {
      const validationMessage = this.validate();
      if (validationMessage)
        return this.$toast.open({ message: validationMessage, type: "info" });

      this.saving = true;
      try {
        const isUpdate = Boolean(this.check.id);
        if (this.check.id) await ServiceCheck.update(this.check);
        else this.check.id = await ServiceCheck.save(this.check);
        this.$toast.open({
          message: isUpdate
            ? "Cheque atualizado com sucesso!"
            : "Cheque salvo com sucesso!",
          type: "success",
        });
        await this.searchChecks(1);
        this.newCheck();
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.erros ||
            error?.response?.data?.message ||
            "Não foi possível salvar o cheque",
          type: "info",
        });
      } finally {
        this.saving = false;
      }
    },
    newCheck() {
      this.check = emptyCheck();
      this.receiptName = "";
      this.destinationName = "";
      this.selectTab(0);

      this.$refs.idCentroCusto.valueItems = "";
      this.$refs.idBanco.valueItems = "";
      this.$refs.situacao.valueItems = "";
      this.$refs.valor.valueInput = "";
    },
    openCheckDeleteConfirmation(id) {
      if (!id) return;
      this.selectedCheckId = id;
      this.$bvModal.show("modal-confirm-check-delete");
    },
    async removeCheck() {
      const id = this.selectedCheckId;
      if (!id) return;
      try {
        await ServiceCheck.delete(id);
        if (this.check.id === id) this.newCheck();
        await this.searchChecks(this.filters.page);
        this.$toast.open({
          message: "Cheque excluído com sucesso!",
          type: "success",
        });
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Não foi possível excluir o cheque",
          type: "info",
        });
      } finally {
        this.selectedCheckId = "";
      }
    },
    async edit(id) {
      try {
        const data = await ServiceCheck.findById(id);
        this.check = {
          ...emptyCheck(),
          ...data,
          dataEntrada: this.normalizeDate(data.dataEntrada),
          dataBomPara: this.normalizeDate(data.dataBomPara),
          valor: data.valor === null ? null : Number(data.valor),
        };
        this.receiptName = data.nomeRecebimento || "";
        this.destinationName = data.nomeDestino || "";
        this.selectTab(0);

        this.$refs.idCentroCusto.valueItems = data.idCentroCusto;
        this.$refs.idBanco.valueItems = data.idBanco;
        this.$refs.situacao.valueItems = data.situacao;
        this.$refs.valor.valueInput = data.valor === null ? "" : Number(data.valor);
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível carregar o cheque",
          type: "info",
        });
      }
    },
    async searchChecks(page = 1) {
      this.loadingSearch = true;
      this.filters.page = Math.max(page, 1);
      try {
        const result = await ServiceCheck.search(this.filters);
        this.checks = result.data || [];
        this.total = result.total || 0;
      } catch (error) {
        this.$toast.open({
          message:
            error?.response?.data?.message ||
            "Não foi possível pesquisar os cheques",
          type: "info",
        });
      } finally {
        this.loadingSearch = false;
      }
    },
    async loadOptions() {
      try {
        const [banks, costCenters, employees] = await Promise.all([
          ServiceCheckBank.findAll(),
          ServiceCheckCostCenter.findAll(),
          api.get("/employees/combobox/fill"),
        ]);
        this.banks = banks;
        this.costCenters = costCenters;
        this.employees = employees.data.data || [];

        console.log(employees);
      } catch (error) {
        this.$toast.open({
          message: "Não foi possível carregar as opções do cadastro",
          type: "info",
        });
      }
    },
    catalogCreated(type, item) {
      if (type === "banks") {
        this.banks.push(item);
        this.banks.sort((a, b) => a.nome.localeCompare(b.nome));
        this.check.idBanco = item.id;
      } else {
        this.costCenters.push(item);
        this.costCenters.sort((a, b) => a.nome.localeCompare(b.nome));
        this.check.idCentroCusto = item.id;
      }
    },
    catalogUpdated(type, item) {
      const catalog = type === "banks" ? this.banks : this.costCenters;
      const index = catalog.findIndex(({ id }) => id === item.id);
      if (index >= 0) this.$set(catalog, index, item);
      catalog.sort((a, b) => a.nome.localeCompare(b.nome));
    },
    catalogDeleted(type, id) {
      if (type === "banks") {
        this.banks = this.banks.filter((item) => item.id !== id);
        if (this.check.idBanco === id) this.check.idBanco = "";
      } else {
        this.costCenters = this.costCenters.filter((item) => item.id !== id);
        if (this.check.idCentroCusto === id) this.check.idCentroCusto = "";
      }
    },
    normalizeDate(date) {
      return date ? moment(date).format("YYYY-MM-DD") : "";
    },
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "--";
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    statusClass(status) {
      return status.toLowerCase().replace(" ", "-");
    },
  },
  mounted() {
    this.loadOptions();
    this.searchChecks(1);
  },
};
</script>

<style scoped>
.checks-page {
  display: flex;
  flex-direction: column;
  margin-left: 80px;
  width: calc(100% - 100px);
  min-height: 100vh;
  padding: 65px 0 78px;
  color: #3a4750;
  font-size: 12px;
}
.checks-tabs /deep/ .tabs {
  align-items: flex-end;
  gap: 1px;
  min-height: 39px;
  padding: 4px 0 0;
  border-radius: 0;
  background: transparent;
}
.checks-tabs /deep/ .customTabs {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #eee;
  color: #000;
  font-size: 13px !important;
  font-weight: 500;
  box-shadow: none;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
.checks-tabs /deep/ .customTabs:hover,
.checks-tabs /deep/ .tabSelected {
  height: 35px;
  padding-bottom: 5px;
  border-color: #006458;
  border-bottom: 2px solid white;
  border-radius: 10px 10px 0 0;
  background: #006458;
  color: white;
  transform: translateY(-4px);
}
.checks-tabs /deep/ .contentItem {
  border-radius: 0 !important;
  background: white;
}
.page-section-title {
  margin: 10px 12px 18px;
  padding: 0 0 6px;
  border-bottom: 1px solid #dedede;
  color: #525252;
  font-size: 16px;
  font-weight: 600;
}
.required-fields {
  display: flex;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e6e8e8;
}
.required-fields > small {
  color: #777;
}
.required-fields > small strong {
  color: #c41515;
}
.floating-actions {
  position: fixed;
  z-index: 200;
  bottom: 0;
  left: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 60px);
  min-height: 58px;
  padding: 10px 28px 10px 32px;
  background: white;
  box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.1);
}
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.action-button {
  min-width: 148px;
  height: 30px;
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  transition: 0.15s ease;
}
.action-button.primary {
  color: white;
  background: #006256;
}
.action-button.primary:hover:not(:disabled) {
  background: #004f46;
  transform: translateY(-1px);
}
.action-button.secondary {
  color: #222;
  background: #d7d7d7;
  border-color: #d7d7d7;
}
.action-button.secondary:hover:not(:disabled) {
  color: #222;
  border-color: #c9c9c9;
  background: #cfcfcf;
}
.action-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}
.editing-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 13px 16px;
  margin-bottom: 16px;
  border: 1px solid #e0e4e3;
  border-left: 5px solid #875d12;
  border-radius: 6px;
  background: #fffaf0;
}
.editing-information {
  display: flex;
  align-items: center;
  gap: 12px;
}
.editing-information > svg {
  color: #006256;
  font-size: 22px;
}
.editing-information div {
  display: flex;
  flex-direction: column;
}
.editing-information small,
.editing-status span {
  color: #777;
}
.editing-information strong {
  color: #273e3b;
  font-size: 14px;
}
.editing-status {
  display: flex;
  align-items: center;
  gap: 10px;
}
.editing-pago,
.editing-compensado {
  border-left-color: #278352;
  background: #f4fbf7;
}
.editing-devolvido,
.editing-cancelado {
  border-left-color: #b33939;
  background: #fff7f7;
}
.form-section {
  border: 1px solid #e2e5e4;
  border-radius: 8px;
  background: white;
  padding: 18px;
  box-shadow: 0 2px 7px rgba(36, 55, 52, 0.04);
}
.section-heading {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 18px;
}
.section-heading.compact {
  margin-bottom: 14px;
}
.section-heading h6 {
  margin: 0;
  color: #273e3b;
  font-size: 14px;
  font-weight: 600;
}
.section-heading small {
  color: #858b89;
}
.section-icon {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  border-radius: 8px;
  color: #006256;
  background: #eaf4f2;
  font-size: 16px;
}
.parties-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.form-card,
.search-card {
  background: #f7f8f8;
  border: 1px solid #e3e7e6;
  border-radius: 8px;
  padding: 20px;
  min-height: 480px;
}
.search-card {
  padding: 12px;
  border: 0;
  border-radius: 0;
  background: white;
}
.form-grid {
  display: grid;
  gap: 25px 14px;
}
.general-fields {
  grid-template-columns: repeat(5, minmax(120px, 1fr));
}
.field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.field label {
  margin: 0 0 4px;
  font-weight: 600;
  color: #666360;
}
.field label span {
  color: #c41515;
}
.field input,
.field select,
.field textarea {
  width: 100%;
  border: 1px solid #d5d3d3;
  border-radius: 4px;
  min-height: 32px;
  padding: 5px 8px;
  color: #5c5855;
  background: white;
  outline: none;
}
.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: #75aaa4;
  box-shadow: 0 0 0 2px rgba(0, 98, 86, 0.08);
}
.field input[readonly] {
  background: #f7f7f7;
}
.select-with-action > div {
  display: flex;
}
.select-with-action select {
  border-radius: 4px 0 0 4px;
}
.select-with-action button {
  width: 34px;
  border: 0;
  border-radius: 0 4px 4px 0;
  background: #006256;
  color: white;
}
.party-section {
  border: 1px dashed #d8dddc;
  border-radius: 6px;
  padding: 14px;
  margin-top: 0;
  background: #fbfcfc;
}
.party-section /deep/ .party-title {
  font-weight: 600;
  color: #666360;
  margin-bottom: 10px;
}
.party-section /deep/ .radio-row {
  display: flex;
  gap: 35px;
  margin-bottom: 16px;
}
.party-section /deep/ .radio-row label {
  cursor: pointer;
  margin: 0;
}
.party-section /deep/ .radio-row input {
  accent-color: #006256;
}
.party-section /deep/ .field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.party-section /deep/ .field label {
  margin: 0 0 4px;
  font-weight: 600;
  color: #666360;
}
.party-section /deep/ .field input {
  width: 100%;
  min-height: 32px;
  border: 1px solid #d5d3d3;
  border-radius: 4px;
  padding: 5px 8px;
  color: #5c5855;
}
.party-section /deep/ .field input[readonly] {
  background: #f7f7f7;
}
.full-field {
  width: 100%;
  margin-top: 18px;
}
.observation-field textarea {
  resize: vertical;
}
.situation-field select {
  font-weight: 600;
}
.situation-em-aberto select {
  color: #875d12;
  border-color: #e3c77f;
  background: #fffaf0;
}
.situation-pago select,
.situation-compensado select {
  color: #17643a;
  border-color: #8fc5a7;
  background: #f4fbf7;
}
.situation-devolvido select,
.situation-cancelado select {
  color: #9c2424;
  border-color: #d9a0a0;
  background: #fff7f7;
}
.search-filters {
  display: grid;
  flex: 1;
  grid-template-columns: 150px minmax(180px, 1fr) 135px 135px 34px;
  gap: 0;
  align-items: end;
}
.search-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  min-height: 38px;
}
.search-filters .field label {
  font-size: 10px;
}
.search-filters .field input,
.search-filters .field select {
  height: 30px;
  min-height: 30px;
  border-radius: 0;
}
.search-filters .field:first-child select {
  border-radius: 5px 0 0 5px;
}
.search-filters .field + .field input {
  border-left: 0;
}
.records-counter {
  display: flex;
  align-items: center;
  align-self: flex-end;
  gap: 5px;
  height: 30px;
  padding: 0 0 0 14px;
  border-left: 1px solid #e1e1e1;
  color: #006458;
  white-space: nowrap;
}
.filter-button {
  width: 34px;
  height: 30px;
  border: 1px solid #c3c1c1;
  border-left: 0;
  border-radius: 0 5px 5px 0;
  padding: 0;
  background: whitesmoke;
  color: #222;
}
.check-table-wrapper {
  margin-top: 12px;
  overflow-x: auto;
}
.check-table {
  width: 100%;
  min-width: 850px;
  border-collapse: separate;
  border-spacing: 0 5px;
  color: #313131;
  text-align: center;
}
.check-table th {
  height: 30px;
  padding: 6px 10px;
  background: #006256;
  color: whitesmoke;
  font-weight: 600;
  white-space: nowrap;
}
.check-table th:first-child {
  border-radius: 5px 0 0 5px;
}
.check-table th:last-child {
  border-radius: 0 5px 5px 0;
}
.actions-heading,
.actions-cell {
  width: 70px;
}
.check-table td {
  height: 40px;
  padding: 6px 10px;
  border-top: 1px solid #e3ebe2;
  border-bottom: 1px solid #e3ebe2;
  background: #f8f8f8;
  vertical-align: middle;
}
.check-table td:first-child {
  border-left: 1px solid #e3ebe2;
  border-radius: 2px 0 0 2px;
}
.check-table td:last-child {
  border-right: 1px solid #e3ebe2;
  border-radius: 0 2px 2px 0;
}
.check-table tbody tr:hover td {
  background: #f2f2f2;
}
.check-row {
  cursor: pointer;
}
.check-row:focus {
  outline: none;
}
.check-row:focus td {
  background: #eef5f4;
}
.check-number {
  color: #006458;
  font-weight: 700;
  text-transform: uppercase;
}
.row-actions {
  display: flex;
  justify-content: center;
  gap: 7px;
}
.row-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 5px;
  background: #e3ebe2;
  color: #006458;
}
.row-actions .delete {
  color: #006458;
}
.row-actions button:hover {
  background: #006458;
  color: white;
}
.empty {
  text-align: center;
  padding: 35px !important;
}
.status {
  display: inline-block;
  border-radius: 12px;
  padding: 3px 9px;
  background: #eceaea;
  white-space: nowrap;
}
.status.pago,
.status.compensado {
  color: #17643a;
  background: #e0f1e7;
}
.status.devolvido,
.status.cancelado {
  color: #9c2424;
  background: #f7e2e2;
}
.status.em-aberto {
  color: #875d12;
  background: #fff0ce;
}
.table-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
}
.pagination {
  display: flex;
  align-items: center;
  gap: 5px;
}
.pagination button,
.pagination span {
  width: 22px;
  height: 22px;
  border: 1px solid #afaeae;
  border-radius: 50%;
  background: whitesmoke;
  color: #006458;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pagination span {
  border-radius: 3px;
  background: #006458;
  color: white;
  font-weight: 600;
}
.pagination button:disabled {
  opacity: 0;
}
@media (max-width: 1100px) {
  .general-fields {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }
  .parties-grid {
    grid-template-columns: 1fr;
  }
  .search-filters {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .search-toolbar {
    align-items: stretch;
    flex-direction: column-reverse;
    gap: 12px;
  }
  .search-filters .field input,
  .search-filters .field select,
  .search-filters .field:first-child select,
  .search-filters .field + .field input {
    border: 1px solid #d5d3d3;
    border-radius: 4px;
  }
  .records-counter {
    align-self: stretch;
    height: auto;
    min-height: 32px;
    border-left: 0;
    border-bottom: 1px solid #e1e1e1;
    padding: 0 0 8px;
  }
  .filter-button {
    width: 100%;
    border: 0;
    border-radius: 4px;
    background: #006256;
    color: white;
  }
}
@media (max-width: 700px) {
  .checks-page {
    margin-left: 0;
    width: 100%;
    padding: 58px 10px 86px;
  }
  .editing-banner {
    align-items: stretch;
    flex-direction: column;
  }
  .editing-status {
    justify-content: space-between;
  }
  .action-buttons {
    flex: 2;
    width: auto;
  }
  .action-button {
    min-width: 0;
    flex: 1;
    padding: 0 8px;
  }
  .floating-actions {
    left: 0;
    width: 100%;
    min-height: 58px;
    gap: 7px;
    padding: 8px 10px;
  }
  .floating-actions > .action-button {
    flex: 1;
  }
  .general-fields,
  .search-filters {
    grid-template-columns: 1fr;
  }
  .form-card,
  .search-card {
    padding: 14px;
    min-height: auto;
  }
  .party-section /deep/ .radio-row {
    gap: 18px;
    flex-wrap: wrap;
  }
  .page-section-title {
    margin-right: 0;
    margin-left: 0;
  }
}
</style>
